import { Helmet } from 'react-helmet-async'

const defaultMeta = {
  title: 'Prompted Thoughts | AI • Apps • DX • Data',
  description:
    'A developer-centric video podcast at the intersection of Generative AI, app development, developer experience, and data. Watch on YouTube or listen on your favorite podcast app.',
  image: '/images/og-image.jpg',
  url: 'https://promptedthoughts.dev',
}

export const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  article = null,
  podcast = null,
}) => {
  const seo = {
    title: title ? `${title} | Prompted Thoughts` : defaultMeta.title,
    description: description || defaultMeta.description,
    image: image || defaultMeta.image,
    url: url || defaultMeta.url,
  }

  // Generate JSON-LD structured data
  const getStructuredData = () => {
    const baseData = {
      '@context': 'https://schema.org',
    }

    if (podcast) {
      // PodcastEpisode schema
      return {
        ...baseData,
        '@type': 'PodcastEpisode',
        name: podcast.title,
        description: podcast.summary,
        datePublished: podcast.date,
        duration: `PT${podcast.duration.replace(':', 'M')}S`,
        partOfSeries: {
          '@type': 'PodcastSeries',
          name: 'Prompted Thoughts',
          url: defaultMeta.url,
        },
        ...(podcast.audioUrl && {
          associatedMedia: {
            '@type': 'MediaObject',
            contentUrl: podcast.audioUrl,
          },
        }),
      }
    }

    // Default WebSite schema
    return {
      ...baseData,
      '@type': 'WebSite',
      name: 'Prompted Thoughts',
      description: defaultMeta.description,
      url: defaultMeta.url,
    }
  }

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />

      {/* Open Graph */}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={type} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />

      {/* Article meta */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.author && <meta property="article:author" content={article.author} />}
          {article.tags && article.tags.map((tag) => <meta key={tag} property="article:tag" content={tag} />)}
        </>
      )}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">{JSON.stringify(getStructuredData())}</script>
    </Helmet>
  )
}

export default SEO
